<template>
  <div class="seal-points-input-page">
    <!-- 顶部导航栏 -->
    <van-nav-bar
      title="录入印章点位"
      left-arrow
      @click-left="handleBack"
    />

    <div class="content-wrapper">
      <!-- 规则选择 -->
      <div class="form-section">
        <van-field
          v-model="selectedRuleRemark"
          label="选择规则"
          placeholder="请选择规则"
          readonly
          is-link
          @click="showRulePicker = true"
        />
        <van-popup v-model="showRulePicker" position="bottom">
          <van-picker
            show-toolbar
            title="选择规则"
            :columns="ruleColumns"
            @confirm="onRuleConfirm"
            @cancel="showRulePicker = false"
          />
        </van-popup>
      </div>

      <!-- 规则信息显示 -->
      <div v-if="currentRule" class="rule-info">
        <p><strong>规则编码:</strong> {{ currentRule.ruleCode }}</p>
        <p><strong>已录入记录数:</strong> {{ existingRulesList.length }}</p>
      </div>

      <!-- 印章区域 -->
      <div class="seal-section">
        <div class="seal-label">用印章按压屏幕记录点位（4个触点）</div>
        <div class="canvas-container">
          <canvas
            ref="canvas"
            width="400"
            height="400"
            @touchstart="handleTouchStart"
            @touchend="handleTouchEnd"
          ></canvas>
          <p class="hint">用印章按压屏幕，确保4个触点同时接触</p>
        </div>
        
        <!-- 点位信息显示 -->
        <div v-if="touchPoints.length > 0" class="points-info">
          <p><strong>当前点位 ({{ touchPoints.length }}/4):</strong></p>
          <p v-for="(point, idx) in touchPoints" :key="idx">
            点{{ idx + 1 }}: [{{ point[0] }}, {{ point[1] }}]
          </p>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="action-section">
        <van-button
          type="warning"
          size="large"
          block
          @click="handleClear"
          :disabled="touchPoints.length === 0"
        >
          清除当前点位
        </van-button>
        <van-button
          type="primary"
          size="large"
          block
          @click="handleAddPoints"
          :disabled="touchPoints.length !== 4 || !currentRule"
          :loading="saving"
          style="margin-top: 12px;"
        >
          新增点位记录
        </van-button>
      </div>

      <!-- 已录入点位列表 -->
      <div v-if="currentRule && existingRulesList.length > 0" class="points-list-section">
        <div class="section-title">已录入的记录 (共{{ existingRulesList.length }}条)</div>
        <div
          v-for="(ruleRecord, idx) in existingRulesList"
          :key="idx"
          class="points-group"
        >
          <div class="group-header">第{{ idx + 1 }}条记录 - 匹配分数: {{ ruleRecord.point }}分</div>
          <div class="group-points">
            <span
              v-for="(point, pIdx) in ruleRecord.points"
              :key="pIdx"
              class="point-item"
            >
              [{{ point[0] }}, {{ point[1] }}]
            </span>
          </div>
        </div>
      </div>

      <!-- 提示信息 -->
      <div class="tip-section">
        <van-notice-bar
          text="提示：每次按压印章后，点击'新增点位记录'按钮，系统会先匹配计算分数，然后新增一条记录。匹配分数会存储到point字段。"
          left-icon="info-o"
          color="#1989fa"
          background="#ecf9ff"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getSealRules, addSealPoints } from '@/api/sealTest'
import { Toast } from 'vant'

export default {
  name: 'SealPointsInput',
  data() {
    return {
      allRules: [],
      ruleColumns: [],
      showRulePicker: false,
      selectedRuleId: null, // 现在存储的是rule_code
      selectedRuleRemark: '',
      currentRule: null, // {ruleCode: 'xxx', remark: 'xxx'}
      existingRulesList: [], // 已录入的记录列表 [{points: [], point: 85}, ...]
      touchPoints: [],
      canvas: null,
      ctx: null,
      saving: false,
      // 标准坐标系（用于跨设备兼容）
      standardWidth: 800,
      standardHeight: 600
    }
  },
  mounted() {
    this.canvas = this.$refs.canvas
    this.ctx = this.canvas.getContext('2d')
    this.loadRules()
  },
  methods: {
    async loadRules() {
      try {
        const res = await getSealRules()
        const rules = res.data || res
        // rules现在是从SQL返回的Map列表，包含rule_code和remark
        this.allRules = rules
        this.ruleColumns = rules.map(rule => ({
          text: rule.remark || rule.rule_code,
          value: rule.rule_code, // 使用rule_code作为value
          ruleCode: rule.rule_code,
          remark: rule.remark
        }))
      } catch (err) {
        console.error('加载规则失败:', err)
        Toast.fail('加载规则失败')
      }
    },
    onRuleConfirm(value) {
      // value现在是rule_code
      const ruleCode = value.value || value
      const rule = this.allRules.find(r => r.rule_code === ruleCode)
      if (rule) {
        this.selectedRuleId = ruleCode // 使用rule_code作为ID
        this.selectedRuleRemark = rule.remark || rule.rule_code
        this.currentRule = {
          ruleCode: rule.rule_code,
          remark: rule.remark
        }
        this.loadExistingRules(rule.rule_code)
        this.showRulePicker = false
      }
    },
    async loadExistingRules(ruleCode) {
      this.existingRulesList = []
      
      try {
        const res = await getSealRules()
        const allRules = res.data || res
        // 筛选出相同ruleCode的所有记录
        const rules = allRules.filter(r => r.ruleCode === ruleCode && r.id !== this.selectedRuleId)
        
        // 解析每条记录的点位
        for (const rule of rules) {
          if (rule.sealPoints) {
            try {
              const points = JSON.parse(rule.sealPoints)
              if (Array.isArray(points) && points.length === 4) {
                this.existingRulesList.push({
                  id: rule.id,
                  points: points,
                  point: rule.point || 0,
                  remark: rule.remark
                })
              }
            } catch (e) {
              console.error('解析点位失败:', e, rule)
            }
          }
        }
      } catch (e) {
        console.error('加载已录入记录失败:', e)
      }
    },
    handleTouchStart(e) {
      e.preventDefault()
      if (e.touches.length > 0) {
        // 清空之前的画布和点位数据
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.touchPoints = []
        
        const rect = this.canvas.getBoundingClientRect()
        const scaleX = this.canvas.width / rect.width
        const scaleY = this.canvas.height / rect.height

        // 收集所有触点的坐标并转换为标准800x600像素坐标
        for (let i = 0; i < e.touches.length; i++) {
          const touch = e.touches[i]
          const x = touch.clientX - rect.left
          const y = touch.clientY - rect.top
          
          // 转换为 canvas 内部坐标（用于绘制）
          const canvasX = x * scaleX
          const canvasY = y * scaleY
          
          // 转换为标准800x600像素坐标（用于对比，保证跨设备兼容）
          const standardX = Math.round((x / rect.width) * this.standardWidth)
          const standardY = Math.round((y / rect.height) * this.standardHeight)
          
          this.touchPoints.push([standardX, standardY])
          this.drawPoint(canvasX, canvasY, i + 1)
        }

        console.log(`${e.touches.length}个触点，点位记录（标准坐标）:`, this.touchPoints)
      }
    },
    handleTouchEnd(e) {
      // 手指离开时不做处理
    },
    drawPoint(x, y, index) {
      // 绘制红色圆点
      this.ctx.fillStyle = '#FF0000'
      this.ctx.beginPath()
      this.ctx.arc(x, y, 8, 0, 2 * Math.PI)
      this.ctx.fill()
      
      // 绘制序号文字
      this.ctx.fillStyle = '#FFFFFF'
      this.ctx.font = 'bold 12px Arial'
      this.ctx.textAlign = 'center'
      this.ctx.textBaseline = 'middle'
      this.ctx.fillText(index.toString(), x, y)
    },
    handleClear() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      this.touchPoints = []
    },
    async handleAddPoints() {
      if (!this.currentRule) {
        Toast.fail('请先选择规则')
        return
      }

      if (this.touchPoints.length !== 4) {
        Toast.fail('必须记录4个点位')
        return
      }

      this.saving = true
      try {
        await addSealPoints({
          ruleCode: this.currentRule.ruleCode,
          sealPoints: this.touchPoints,
          coordinateSystem: 1
        })

        Toast.success('点位记录新增成功！')
        
        // 重新加载规则列表和已录入记录
        await this.loadRules()
        if (this.currentRule && this.currentRule.ruleCode) {
          await this.loadExistingRules(this.currentRule.ruleCode)
        }
        
        // 清空当前点位
        this.handleClear()
      } catch (error) {
        console.error('追加点位失败:', error)
        Toast.fail(error.message || '追加点位失败，请重试')
      } finally {
        this.saving = false
      }
    },
    handleBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped lang="less">
.seal-points-input-page {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.content-wrapper {
  padding: 12px;
}

.form-section {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.rule-info {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #333;
}

.rule-info p {
  margin: 4px 0;
}

.seal-section {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.seal-label {
  font-size: 14px;
  color: #333;
  margin-bottom: 12px;
  font-weight: 500;
}

.canvas-container {
  background: white;
  margin: 12px 0;
  border-radius: 4px;
  text-align: center;
  padding: 12px;
}

canvas {
  border: 1px solid #ddd;
  cursor: crosshair;
  max-width: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 8px;
}

.hint {
  color: #999;
  font-size: 12px;
  margin-top: 8px;
}

.points-info {
  background: white;
  padding: 12px;
  margin: 12px 0;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
}

.points-info p {
  margin: 4px 0;
}

.action-section {
  margin-bottom: 12px;
}

.points-list-section {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.section-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 12px;
}

.points-group {
  background: #f9f9f9;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 8px;
}

.group-header {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.group-points {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.point-item {
  font-size: 12px;
  color: #333;
  background: #fff;
  padding: 2px 6px;
  border-radius: 2px;
}

.tip-section {
  margin-top: 12px;
}
</style>

